import { Alert, Dialog } from "@tm/components"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocalization } from "@tm/localization"
import { CreateCustomList, EditCustomList } from "@tm/data/basket"
import { FormProvider, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Override } from "@tm/models"
import { EditDialogBody } from "./DialogBody"
import { EditDialogHeader } from "./DialogHeader"
import { useSchema } from "./schema"

export type DialogCustomListCreate = CreateCustomList & { type: "create" }
export type DialogCustomListEdit = EditCustomList & { type: "edit" }
export type DialogCustomList = DialogCustomListCreate | DialogCustomListEdit

type EditDialogContentProps = {
    compilation: DialogCustomList
    saveError?: string
    isClosing?: boolean // As long the closing animation is running we should have the save button disabled, to prevent double save
    onClose(): void
    onSave(list: DialogCustomList): void
}

function EditDialogContent({ onClose, onSave, compilation, saveError, isClosing }: EditDialogContentProps) {
    const { translateText } = useLocalization()
    const schema = useSchema()

    const headline = compilation.type === "edit" ? translateText(13925) : translateText(13948)

    const defaultValues = useMemo(() => {
        if (compilation.type === "create") {
            return {
                name: "",
            }
        }
        return compilation
    }, [compilation])

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const { handleSubmit } = form

    function handleConfirmButtonClick(data: z.infer<typeof schema>) {
        onSave({ ...compilation, ...data })
    }

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                <EditDialogHeader headline={headline} onCloseButtonClick={onClose} sx={{ mb: 2 }} isClosing={isClosing} />
                {saveError && <Alert size="small" severity="error" title={saveError} variant="outlined" sx={{ mb: 2 }} />}
                <EditDialogBody />
            </form>
        </FormProvider>
    )
}

type EditDialogProps = Override<
    EditDialogContentProps,
    {
        compilation?: EditDialogContentProps["compilation"]
    }
>

export function CompilationEditDialog(props: EditDialogProps) {
    const [open, setOpen] = useState(false)

    const { compilation, onClose, ...restProps } = props

    const [editableCompilation, setEditableCompilation] = useState<DialogCustomList | undefined>()

    const handleClose = useCallback(() => {
        setOpen(false)
    }, [])

    const handleExited = useCallback(() => {
        setEditableCompilation(undefined)
        onClose()
    }, [])

    useEffect(() => {
        if (compilation) {
            setOpen(true)
            setEditableCompilation(compilation)
        } else {
            handleClose()
        }
    }, [compilation])

    return (
        <Dialog open={open} position="middle" maxWidth="lg" onExited={handleExited}>
            {editableCompilation && (
                <EditDialogContent {...restProps} compilation={editableCompilation} onClose={handleClose} isClosing={!compilation} />
            )}
        </Dialog>
    )
}
